/**
 * CustomizedDropdown - A reusable dropdown component
 * 
 * Usage in HTML:
 * <customized-dropdown data-form-id="optional-form-id">
 *   <button type="button" class="customized-dropdown__trigger" data-action="toggle-dropdown">
 *     <span class="customized-dropdown__trigger-text" data-selected-value>Selected Value</span>
 *     <span class="svg-wrapper">{{ 'icon-caret.svg' | inline_asset_content }}</span>
 *   </button>
 *   <div class="customized-dropdown__panel" hidden>
 *     <button type="button" class="customized-dropdown__item" data-value="option1">Option 1</button>
 *     <button type="button" class="customized-dropdown__item" data-value="option2">Option 2</button>
 *   </div>
 *   <select class="customized-dropdown__native-select" name="field_name" hidden>
 *     <option value="option1">Option 1</option>
 *     <option value="option2" selected>Option 2</option>
 *   </select>
 * </customized-dropdown>
 * 
 * Events emitted:
 * - 'customized-dropdown:change' - Fired when selection changes, detail: { value, text, dropdown }
 */

if (!customElements.get('customized-dropdown')) {
class CustomizedDropdown extends HTMLElement {
  constructor() {
    super();
    this.trigger = null;
    this.triggerText = null;
    this.panel = null;
    this.nativeSelect = null;
    this.items = [];
    this.isOpen = false;
    this.clickOutsideHandler = null;
    this.keydownHandler = null;
    this.focusedIndex = -1;
  }

  connectedCallback() {
    // Support multiple class naming conventions for flexibility
    this.trigger = this.querySelector('[data-action="toggle-dropdown"], [data-action="toggle-font-dropdown"], .customized-dropdown__trigger');
    this.triggerText = this.querySelector('.customized-button__text, .customized-dropdown__trigger-text, [data-selected-value]');
    this.panel = this.querySelector('.customized-dropdown__panel, .customized-facets-dropdown, .facets-dropdown');
    this.nativeSelect = this.querySelector('.customized-dropdown__native-select, select[name]');
    this.items = Array.from(this.querySelectorAll('.customized-dropdown__item, .facets-dropdown__item'));

    if (!this.trigger || !this.panel) {
      console.warn('CustomizedDropdown: Missing required elements (trigger or panel)');
      return;
    }

    this.setupTrigger();
    this.setupPanel();
    this.setupItems();
    this.setupKeyboardNavigation();
    this.setupClickOutside();
    this.updateSelectedState();
  }

  disconnectedCallback() {
    if (this.clickOutsideHandler) {
      document.removeEventListener('click', this.clickOutsideHandler);
    }
    if (this.keydownHandler) {
      document.removeEventListener('keydown', this.keydownHandler);
    }
  }

  setupTrigger() {
    this.trigger.addEventListener('click', (e) => {
      e.stopPropagation();
      this.toggle();
    });

    // Set initial aria attributes
    this.trigger.setAttribute('aria-haspopup', 'listbox');
    this.trigger.setAttribute('aria-expanded', 'false');
  }

  setupPanel() {
    // Stop propagation on panel clicks to prevent parent handlers (like menu-drawer) from closing
    this.panel.addEventListener('click', (e) => {
      e.stopPropagation();
    });
  }

  setupItems() {
    this.items.forEach((item, index) => {
      item.setAttribute('role', 'option');
      item.setAttribute('tabindex', '-1');

      item.addEventListener('click', (e) => {
        e.stopPropagation();
        this.selectItem(item);
      });
    });
  }

  setupKeyboardNavigation() {
    this.keydownHandler = (e) => {
      if (!this.isOpen) return;

      switch (e.key) {
        case 'Escape':
          e.preventDefault();
          this.close();
          this.trigger.focus();
          break;
        case 'ArrowDown':
          e.preventDefault();
          this.focusNextItem();
          break;
        case 'ArrowUp':
          e.preventDefault();
          this.focusPreviousItem();
          break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          if (this.focusedIndex >= 0 && this.focusedIndex < this.items.length) {
            this.selectItem(this.items[this.focusedIndex]);
          }
          break;
        case 'Tab':
          this.close();
          break;
      }
    };

    document.addEventListener('keydown', this.keydownHandler);
  }

  setupClickOutside() {
    this.clickOutsideHandler = (e) => {
      if (this.isOpen && !this.contains(e.target)) {
        this.close();
      }
    };

    document.addEventListener('click', this.clickOutsideHandler);
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    this.isOpen = true;
    this.panel.removeAttribute('hidden');
    this.trigger.setAttribute('aria-expanded', 'true');
    
    // Find currently selected item and focus it
    const selectedItem = this.items.find(item => item.classList.contains('selected'));
    if (selectedItem) {
      this.focusedIndex = this.items.indexOf(selectedItem);
    } else {
      this.focusedIndex = 0;
    }
    this.updateFocusedItem();
    
    // Dispatch open event
    this.dispatchEvent(new CustomEvent('customized-dropdown:open', {
      bubbles: true,
      detail: { dropdown: this }
    }));
  }

  close() {
    this.isOpen = false;
    this.panel.setAttribute('hidden', '');
    this.trigger.setAttribute('aria-expanded', 'false');
    this.focusedIndex = -1;
    this.clearFocusedItem();
    
    // Dispatch close event
    this.dispatchEvent(new CustomEvent('customized-dropdown:close', {
      bubbles: true,
      detail: { dropdown: this }
    }));
  }

  selectItem(item) {
    const value = item.dataset.value || item.dataset.fontValue;
    const text = item.textContent.trim();

    // Update selected state
    this.items.forEach(i => i.classList.remove('selected'));
    item.classList.add('selected');

    // Update trigger text
    if (this.triggerText) {
      this.triggerText.textContent = text;
    }

    // Update native select if present
    if (this.nativeSelect) {
      this.nativeSelect.value = value;
      
      // Dispatch input event on native select to trigger form handlers
      this.nativeSelect.dispatchEvent(new Event('input', { bubbles: true }));
      this.nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // Close dropdown
    this.close();

    // Dispatch custom change event
    this.dispatchEvent(new CustomEvent('customized-dropdown:change', {
      bubbles: true,
      detail: { value, text, dropdown: this }
    }));
  }

  updateSelectedState() {
    if (!this.nativeSelect) return;

    const selectedValue = this.nativeSelect.value;
    this.items.forEach(item => {
      const itemValue = item.dataset.value || item.dataset.fontValue;
      if (itemValue === selectedValue) {
        item.classList.add('selected');
      } else {
        item.classList.remove('selected');
      }
    });
  }

  focusNextItem() {
    this.focusedIndex = (this.focusedIndex + 1) % this.items.length;
    this.updateFocusedItem();
  }

  focusPreviousItem() {
    this.focusedIndex = (this.focusedIndex - 1 + this.items.length) % this.items.length;
    this.updateFocusedItem();
  }

  updateFocusedItem() {
    // Focus the item for keyboard navigation (no visual class added)
    if (this.focusedIndex >= 0 && this.focusedIndex < this.items.length) {
      this.items[this.focusedIndex].focus();
    }
  }

  clearFocusedItem() {
    // No-op since we no longer add the focused class
  }

  // Public method to programmatically select a value
  setValue(value) {
    const item = this.items.find(i => (i.dataset.value || i.dataset.fontValue) === value);
    if (item) {
      this.selectItem(item);
    }
  }

  // Public method to get current value
  getValue() {
    return this.nativeSelect ? this.nativeSelect.value : null;
  }
}

// Define the custom element
customElements.define('customized-dropdown', CustomizedDropdown);
}

